/**
 * One download per attachment, shared between previewing it and reading it with AI.
 *
 * ---- Why this exists ----
 *
 * `mail.get_email` returns `attachments[].file_url` — a PATH, not the bytes. So the spreadsheet
 * or PDF genuinely is not in the browser after the mail loads, and both the preview
 * (`AttachmentPreview.tsx`) and the AI-extraction fallback (`useMailExtraction.ts`'s
 * `prepareAttachment`) have to fetch it from `/private/files/…`.
 *
 * They were each doing that with their own bare `fetch`, so previewing an attachment and then
 * running "Read this mail with AI" — the ordinary order of doing things — downloaded the same
 * file twice over the same session. This routes both through one React Query entry keyed by URL,
 * so the second caller gets the bytes already in hand.
 *
 * ---- Why a Blob, and why it is dropped quickly ----
 *
 * The blob is cached, not the parsed result: the two callers want different things out of the
 * same bytes (a rendered sheet vs. TSV for a model), and parsing is cheap next to the download.
 *
 * `gcTime` is deliberately short. These are whole spreadsheets and PDFs held in memory, and the
 * window that matters is "preview, then extract" — a minute, not a session. Caching them for the
 * session would trade a duplicate request for unbounded memory, which is the worse deal.
 *
 * ---- Failures are values elsewhere, but throws here ----
 *
 * `fetchAndParseXlsxPreview` and `fetchPdfBytes` turn every failure into a typed result rather
 * than a throw, and they keep doing so — they catch around this. Throwing here is what lets React
 * Query know NOT to cache a failed download, so a retry actually retries.
 */

import { useMemo } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { proxyBearer } from './frappeSession'

/** Long enough to cover "preview it, then extract it"; short enough not to hoard spreadsheets. */
const ATTACHMENT_BLOB_GC_MS = 2 * 60_000

/** A file at a given URL does not change under us — the URL is content-addressed by Frappe's
 * `File` docname, so a re-fetch within the window can only return the same bytes. */
const ATTACHMENT_BLOB_STALE_MS = 2 * 60_000

export const attachmentBlobKey = (url: string) => ['attachment-blob', url] as const

/**
 * Downloads `url` with the session cookie — and, deployed, the Clerk bearer: `server/index.ts`
 * gates every `/api/*` request on it and answers 401 before Frappe is asked (the pdf preview's
 * "server returned an error (401)", 2026-09-21). Same source as `bosun.ts`; `null` in dev.
 * Exported for the default (uncached) path and tests.
 */
export async function downloadAttachmentBlob(url: string): Promise<Blob> {
  const bearer = await proxyBearer()
  const response = await fetch(url, { credentials: 'include', headers: bearer ? { Authorization: `Bearer ${bearer}` } : {} })
  if (!response.ok) throw new Error(`The server returned an error (${response.status}).`)
  return response.blob()
}

/**
 * A loader that reads through the query cache. Pass it to `fetchAndParseXlsxPreview` /
 * `fetchPdfBytes` / `prepareAttachment`; concurrent callers for the same URL share one in-flight
 * request, and a caller arriving within the window gets the bytes without a request at all.
 */
export function cachedAttachmentLoader(queryClient: QueryClient): (url: string) => Promise<Blob> {
  return (url) =>
    queryClient.fetchQuery({
      queryKey: attachmentBlobKey(url),
      queryFn: () => downloadAttachmentBlob(url),
      staleTime: ATTACHMENT_BLOB_STALE_MS,
      gcTime: ATTACHMENT_BLOB_GC_MS,
      // A failed download must not be retried behind the caller's back — both call sites report
      // their own failure and offer their own retry.
      retry: false,
    })
}

/** What every consumer of the above accepts: given a URL, hand back the bytes. */
export type BlobLoader = (url: string) => Promise<Blob>

/**
 * The loader for components. Memoised on the query client, so it is referentially stable and safe
 * to name in a `useEffect` dependency list — an unstable loader there would re-run the effect on
 * every render and re-download the very file this module exists to download once.
 */
export function useCachedAttachmentLoader(): BlobLoader {
  const queryClient = useQueryClient()
  return useMemo(() => cachedAttachmentLoader(queryClient), [queryClient])
}
